import { lazy, Suspense } from 'react';

import { NavLink, Redirect, Route, RouteProps, Switch } from 'react-router-dom';

import { useIndividualSubscriptionByIdQuery } from '@m/hooks/data/admin/individualSubscriptions';
import { AdminRouterParams } from '@m/types';
import { RoleName } from '@m/types/api/iam/Iam';
import { DetailPageWrapper, FormPageWrapper, ListPageWrapper } from 'apps/admin/AdminApp.styled';
import AuthenticatedRoute from 'apps/admin/components/AuthenticatedRoute';
import { navLinkStyle } from 'apps/admin/components/BreadCrumbs/active';
import { DynamicCrumb } from 'apps/admin/components/BreadCrumbs/DynamicCrumb';
import { DISPLAY_SURVEY_ROLES } from 'apps/admin/utils/displaySurveyAccess';

const EditZoneContainer = lazy(() => import('apps/admin/containers/location/EditZoneContainer'));
const LocationDetailContainer = lazy(
  () => import('apps/admin/containers/location/LocationDetailContainer'),
);
const ZoneInfoContainer = lazy(
  () => import('apps/admin/containers/location/ZoneInfo/ZoneInfoContainer'),
);
const IndividualSubscriptionContainer = lazy(
  () =>
    import('apps/admin/containers/location/IndividualSubscriptionContainer/IndividualSubscriptionContainer'),
);
const LocationListContainer = lazy(
  () => import('apps/admin/containers/location/LocationListContainer'),
);
const LaneDetailContainer = lazy(
  () => import('apps/admin/containers/location/LaneDetailContainer'),
);
const AddDisplaySurveyContainer = lazy(
  () => import('apps/admin/components/AddDisplaySurvey/AddDisplaySurveyContainer'),
);
const AddLaneContainer = lazy(() => import('apps/admin/components/AddLane/AddLaneContainer'));

const IndividualSubscriptionBreadcrumb = ({ params }: { params: AdminRouterParams }) => {
  const { data: individualSubscription } = useIndividualSubscriptionByIdQuery(
    params.individualSubscriptionId,
  );

  return individualSubscription?.subscriptionPlanAttributes.displayText;
};

export const locationRoutesConfig = [
  // Redirects
  // Zones used to be called locations
  {
    exact: true,
    path: '/location',
    component: () => (
      <Redirect
        to={{
          pathname: '/zones',
        }}
      />
    ),
  },
  {
    path: '/location/',
    component: ({ location }: RouteProps) => (
      <Redirect
        to={{
          pathname: location ? location.pathname.replace('/location/', '/zones/') : '/zones',
        }}
      />
    ),
  },
  // Routes
  {
    exact: true,
    path: '/zones',
    breadcrumb: 'Zones',
    component: AuthenticatedRoute,
    children: (
      <Suspense fallback={null}>
        <ListPageWrapper>
          <LocationListContainer />
        </ListPageWrapper>
      </Suspense>
    ),
  },
  {
    path: '/zones/:zoneId',
    breadcrumb: ({ match: { params } }: { match: { params: AdminRouterParams } }) => (
      <NavLink to={`/zones/${params.zoneId}/info`} activeStyle={navLinkStyle}>
        <DynamicCrumb itemType="zones" itemId={params.zoneId} />
      </NavLink>
    ),
  },
  {
    exact: true,
    path: '/zones/:zoneId/details',
    component: AuthenticatedRoute,
    children: (
      <Suspense fallback={null}>
        <DetailPageWrapper>
          <LocationDetailContainer />
        </DetailPageWrapper>
      </Suspense>
    ),
  },
  {
    exact: true,
    path: '/zones/:zoneId/info',
    component: AuthenticatedRoute,
    children: (
      <Suspense fallback={null}>
        <DetailPageWrapper>
          <ZoneInfoContainer />
        </DetailPageWrapper>
      </Suspense>
    ),
  },
  {
    exact: true,
    path: '/zones/:zoneId/edit',
    breadcrumb: 'Edit',
    roles: [RoleName.OPERATIONAL_ADMIN],
    component: AuthenticatedRoute,
    children: (
      <Suspense fallback={null}>
        <FormPageWrapper>
          <EditZoneContainer />
        </FormPageWrapper>
      </Suspense>
    ),
  },
  {
    exact: true,
    path: '/zones/:zoneId/lane/:laneId',
    roles: [RoleName.OPERATIONAL_ADMIN],
    breadcrumb: ({ match: { params } }: { match: { params: AdminRouterParams } }) => (
      <NavLink to={`/zones/${params.zoneId}/lane/${params.laneId}`} activeStyle={navLinkStyle}>
        <DynamicCrumb itemType="location-lane" itemId={params.laneId} />
      </NavLink>
    ),
    component: AuthenticatedRoute,
    children: (
      <Suspense fallback={null}>
        <DetailPageWrapper>
          <LaneDetailContainer />
        </DetailPageWrapper>
      </Suspense>
    ),
  },
  {
    exact: true,
    path: '/zones/:zoneId/lanes/:laneSiteApiId/add-display',
    breadcrumb: '',
    roles: DISPLAY_SURVEY_ROLES,
    component: AuthenticatedRoute,
    children: (
      <Suspense fallback={null}>
        <AddDisplaySurveyContainer />
      </Suspense>
    ),
  },
  {
    exact: true,
    path: '/zones/:zoneId/lanes/:laneSiteApiId/add-display/:surveyId',
    roles: DISPLAY_SURVEY_ROLES,
    component: AuthenticatedRoute,
    children: (
      <Suspense fallback={null}>
        <AddDisplaySurveyContainer isApprovalFlow />
      </Suspense>
    ),
  },
  {
    exact: true,
    path: '/zones/:zoneId/lanes/:laneSiteApiId/add-display/:surveyId/edit',
    roles: DISPLAY_SURVEY_ROLES,
    component: AuthenticatedRoute,
    children: (
      <Suspense fallback={null}>
        <AddDisplaySurveyContainer isEditing />
      </Suspense>
    ),
  },
  {
    exact: true,
    path: '/zones/:zoneId/add-lane',
    breadcrumb: '',
    component: AuthenticatedRoute,
    children: (
      <Suspense fallback={null}>
        <AddLaneContainer />
      </Suspense>
    ),
  },
  {
    exact: true,
    path: '/zones/:zoneId/add-lane/:addLaneId',
    component: AuthenticatedRoute,
    children: (
      <Suspense fallback={null}>
        <AddLaneContainer isApprovalFlow />
      </Suspense>
    ),
  },
  {
    exact: true,
    path: '/zones/:zoneId/add-lane/:addLaneId/edit',
    component: AuthenticatedRoute,
    children: (
      <Suspense fallback={null}>
        <AddLaneContainer isEditing />
      </Suspense>
    ),
  },
  {
    exact: true,
    path: '/zones/:zoneId/subscriptions',
    breadcrumb: ({ match: { params } }: { match: { params: AdminRouterParams } }) => (
      <NavLink to={`/zones/${params.zoneId}/info?tab=subscriptions`} activeStyle={navLinkStyle}>
        Individual Subscriptions
      </NavLink>
    ),
  },
  {
    exact: true,
    path: '/zones/:zoneId/subscriptions/:individualSubscriptionId',
    breadcrumb: ({ match: { params } }: { match: { params: AdminRouterParams } }) => (
      <IndividualSubscriptionBreadcrumb params={params} />
    ),
    component: AuthenticatedRoute,
    children: (
      <Suspense fallback={null}>
        <IndividualSubscriptionContainer />
      </Suspense>
    ),
  },
];

export default function LocationRoutes() {
  return (
    <Switch>
      <Route
        exact
        path="/zone/:zoneId"
        render={(props) => (
          <Redirect
            to={{
              pathname: `/zones/${props.match.params.zoneId}/info`,
              search: new URLSearchParams({ tab: 'scoutedLocation' }).toString(),
            }}
          />
        )}
      />
      {locationRoutesConfig.map(
        ({ component: Component, breadcrumb, ...props }) =>
          Component && <Component key={props.path} {...props} />,
      )}
    </Switch>
  );
}
